import React, { useCallback, useEffect, useState } from 'react';
import {
  Flex,
  Box,
  Checkbox,
  VStack,
  Button,
  HStack,
  Heading
} from '@chakra-ui/react';
import Tooltip from '@components/atoms/Tooltip';
import HeaderFont from '@components/atoms/HeaderFont';
import { navigate } from 'gatsby';

const FilterBody = ({
  filterItems = [],
  appliedFilters = [],
  onFiltersApplied = (filters) => {},
  onFiltersCleared = () => {}
}) => {
  const [activeFilters, setActiveFilters] = useState([]);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [lastActiveGroupId, setLastActiveGroupId] = useState(null);

  const handleFilterClicked = useCallback(
    ({ active, id, title, groupId = null }) => {
      if (groupId !== lastActiveGroupId && lastActiveGroupId !== null) {
        setActiveFilters([{ title, id }]);
      } else if (active) {
        setActiveFilters([...activeFilters, { title, id }]);
      } else {
        setActiveFilters([
          ...activeFilters.filter((filter) => filter.id !== id)
        ]);
      }

      setActiveTooltip(null);
      setLastActiveGroupId(groupId);
    },
    [activeFilters, lastActiveGroupId]
  );

  const handleApplyClicked = () => {
    setActiveTooltip(null);
    const queryStringItems = activeFilters.map((item) => item.id).join(`,`);
    navigate(`/products?filter=${queryStringItems}`);
  };

  const handleResetClicked = () => {
    setActiveFilters([]);
    onFiltersCleared([]);
    setActiveTooltip(null);
  };

  const handleTooltipToggle = (index = null) => {
    if (index === null) {
      setActiveTooltip(null);
      return;
    }

    if (activeTooltip === index) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(index);
    }
  };

  useEffect(() => {
    setActiveFilters(appliedFilters);
  }, [appliedFilters]);

  return (
    <Flex direction="column" height="100%">
      <Heading
        flex="0 0 auto"
        as="h3"
        pb="1.5rem"
        textTransform="uppercase"
        fontSize="4xl"
        fontWeight="900"
        color="purple.600">
        Filter
      </Heading>
      <Flex direction="column" overflowY="auto">
        {filterItems.map((group, index) => {
          return (
            <React.Fragment key={`${group.title}${index + 1}`}>
              <Flex mb="2">
                <Box as="h4" fontSize="md" fontWeight="700" color="purple.600">
                  {group.title}
                </Box>
                {group.tooltip && (
                  <Tooltip
                    label={group.tooltip}
                    isOpen={activeTooltip === index}>
                    <Box
                      onClick={() => handleTooltipToggle(index)}
                      onMouseLeave={() => handleTooltipToggle()}
                      as="span"
                      pt="0.1rem"
                      pl="0.5rem"
                      color="purple.600"
                      fontSize="sm"
                      cursor="pointer"
                      textDecoration="underline">
                      What is this?
                    </Box>
                  </Tooltip>
                )}
              </Flex>
              <VStack
                align="flex-start"
                pb="1.5rem"
                ml="3px"
                sx={{
                  '.check-box .chakra-checkbox__control': {
                    borderRadius: `0`,
                    borderColor: `gray.400`,
                    borderStyle: `solid`,
                    borderWidth: `1px`,
                    w: `6`,
                    h: `6`
                  }
                }}>
                {group.filters.map((filter) => {
                  return (
                    <Checkbox
                      className="check-box"
                      key={`groupFilters${filter.id + 1}`}
                      value={filter.id}
                      size="lg"
                      colorScheme="teal"
                      isChecked={activeFilters.some(
                        ({ id }) => id === filter.id
                      )}
                      onChange={(e) => {
                        handleFilterClicked({
                          active: e.target.checked,
                          title: filter.title,
                          id: filter.id,
                          groupId: group.groupId
                        });
                      }}>
                      <Box fontSize="sm" fontWeight={700}>
                        {filter.title}
                      </Box>
                    </Checkbox>
                  );
                })}
              </VStack>
            </React.Fragment>
          );
        })}
      </Flex>
      <HStack spacing="1rem" flex="0 0 auto" pt="2rem">
        <Button variant="ghost" onClick={handleResetClicked}>
          <HeaderFont lineHeight="1" fontWeight="700" textTransform="uppercase">
            Clear
          </HeaderFont>
        </Button>
        <Button variant="purple" onClick={handleApplyClicked}>
          <HeaderFont lineHeight="1" fontWeight="700" textTransform="uppercase">
            Apply
          </HeaderFont>
        </Button>
      </HStack>
    </Flex>
  );
};

export default FilterBody;
